import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { useModal } from "../../../utils/hooks/useModal";
import { useCompanyDateFormat } from "../../../utils/hooks/company";
import { formatDatesInText } from "../../../utils/helpers/date";

const hours = (value) => Number(value || 0).toFixed(2);

/** What a scheduled (future-dated) plan change will do, in one sentence. */
export const describeScheduledChange = (change, t, dateFormat) => {
	const date = change.effectiveDate ? moment(change.effectiveDate).format(dateFormat) : "";
	if (change.mode === "payout") {
		return t("scheduled-payout-description", { plan: change.planCode, date });
	}
	return change.mode === "transfer"
		? t("scheduled-transfer-description", { from: change.sourcePlanCode, to: change.planCode, date })
		: t("scheduled-assign-description", { plan: change.planCode, hours: hours(change.amount), date });
};

/** Why a scheduled change has not been applied (refused on its day, or failed), or nothing. */
export const scheduledChangeProblem = (change, t, dateFormat) => {
	if (change.status === "failed") {
		return t("scheduled-change-failed", { reason: formatDatesInText(change.failedReason || "", dateFormat) });
	}
	if (change.blockedReasons?.length) {
		return t("scheduled-change-blocked", { reason: formatDatesInText(change.blockedReasons.join("; "), dateFormat) });
	}
	return null;
};

/**
 * Profile > Time off: the employee's scheduled plan changes (settings PtoScheduledChangeService),
 * what each will do and when, with Cancel (confirmed in a modal).
 */
function ScheduledChanges({ changes, cancel }) {
	const { t } = useTranslation();
	const dateFormat = useCompanyDateFormat();

	const head = useMemo(() => (
		<h2 className="mb-0 text-center font-weight-bolder">{t("cancel-scheduled-change")}</h2>
	), [t]);
	const icon = useMemo(() => (
		<FontAwesomeIcon icon={faCalendarDays} className="text-warning w-100 mb-5" fontSize={70} />
	), []);
	const { open, modal } = useModal({
		head,
		icon,
		color: "danger",
		message: (change) => change
			? (
				<div>
					<div className="mb-2">{describeScheduledChange(change, t, dateFormat)}</div>
					<div>{t("cancel-scheduled-change-confirm")}</div>
				</div>
			)
			: null,
		onConfirm: (change) => change && cancel(change.id),
	});

	if (!changes?.length) {
		return modal;
	}

	return (
		<>
			<div className="border rounded mx-3 mb-3 p-2 small">
				<div className="font-weight-bold mb-1">
					<FontAwesomeIcon icon={faCalendarDays} className="text-info mr-2" />
					{t("scheduled-changes")}
				</div>
				{changes.map((change) => {
					const problem = scheduledChangeProblem(change, t, dateFormat);
					return (
						<div key={change.id} className="d-flex align-items-start justify-content-between gap-2 py-1 border-top">
							<div style={{ whiteSpace: "normal" }}>
								<div>{describeScheduledChange(change, t, dateFormat)}</div>
								{change.comment && <div className="text-muted">{change.comment}</div>}
								{problem && <div className="text-danger">{problem}</div>}
							</div>
							<span
								className="text-danger font-weight-bold cursor-pointer text-nowrap"
								onClick={() => open(change)}
							>
								{t("cancel-scheduled-change")}
							</span>
						</div>
					);
				})}
			</div>
			{modal}
		</>
	);
}

export default ScheduledChanges;
